import {
  Drawer,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Select from "react-select";
import StyledSelect from "../../../product/all-products/components/custom/StyledSelect";
import { PosCustomButton } from "../../../../../customComponents/PosCustomButton/PosCustomButton";
import {
  createAdjustmentLogs,
  getBatchDetailsById,
  getProductDetailsIMS,
  uploadImageBase64,
} from "../../../../../config/Services";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";

const AdjustStockCreationModal = ({ open, handleClose, reset, setReset }) => {
  // State Management
  const [productSearch, setProductSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [adjustments, setAdjustments] = useState([
    {
      id: Date.now(),
      batchId: "",
      availableQuantity: "",
      adjustmentType: "",
      adjustmentQuantity: "",
      reason: "",
      images: [],
    },
  ]);

  const [searchTimer, setSearchTimer] = useState(null);
  const [uploadingImage, setUploadingImage] = useState({});
  const [batchErrors, setBatchErrors] = useState({});
  const [quantityErrors, setQuantityErrors] = useState({});
  const [sellingUnit, setSellingUnit] = useState("");

  const licenseId = localStorage.getItem("licenseId");
  const sessionId = localStorage.getItem("sessionId");
  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  let retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));
  const useAlert = useSnackbar();

  const adjustmentTypeOptions = [{ value: "REDUCE", label: "Reduce" }];

  const reasonOptions = [
    // { value: "STOCK ADDITION", label: "Stock Addition" },
    { value: "stock reduction", label: "Stock Reduction" },
    { value: "manual adjustment", label: "Manual Adjustment" },
    { value: "wastage", label: "Wastage" },
    { value: "shrinkage", label: "Shrinkage" },
    { value: "theft", label: "Theft" },
    { value: "store use", label: "Store use" },
    { value: "damaged", label: "Damaged" },
    { value: "expired", label: "Expired" },
    { value: "product not found", label: "Product not found" },
    { value: "others", label: "Others" },
  ];

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "40px",
      borderRadius: "13px",
      borderColor: state.isFocused ? "#2196f3" : "#E6E6E6",
      boxShadow: state.isFocused ? "0 0 0 1px #2196f3" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#2196f3" : "#999",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 8px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#999",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.5rem",
      marginTop: "4px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#2196f3" : state.isFocused ? "#e3f2fd" : "white",
      color: state.isSelected ? "white" : "#333",
      "&:active": {
        backgroundColor: "#2196f3",
      },
    }),
  };

  // Fetch products from API
  const fetchProducts = async (searchQuery) => {
    if (selectedProduct) {
      return;
    }

    if (!searchQuery || searchQuery.trim().length < 2) {
      setProductOptions([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const payload = {
        pageNumber: 1,
        pageSize: 20,
        categoryList: [],
        locationId: locId,
        brandList: [],
        storageId: [],
        gtinList: [],
        storageIds: [],
        vendorIds: [],
        orgId: orgId,
        outOfStock: false,
        searchBox: searchQuery,
      };
      const response = await getProductDetailsIMS(payload);

      const productsArray = response?.data?.data?.data?.data;

      if (productsArray && Array.isArray(productsArray)) {
        const transformedProducts = productsArray?.map((product) => ({
          id: product?.gtin || product?.GTIN,
          name: product?.itemName || product?.gtin || product?.GTIN,
          gtin: product?.gtin || product?.GTIN,
          fullData: product,
        }));
        setProductOptions(transformedProducts);
      } else {
        setProductOptions([]);
      }
    } catch (error) {
      setProductOptions([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchBatchData = async (gtin) => {
    if (!gtin) return;

    setLoadingBatches(true);
    try {
      const response = await getBatchDetailsById(gtin, locId);

      const result = response?.data;

      if (result && result?.data) {
        const transformedBatches = result?.data?.data?.map((batch) => ({
          value: batch?.batchNo || batch?.batch_id,
          label: batch?.batchNo || batch?.batch_id,
          fullData: batch,
        }));
        setBatchOptions(transformedBatches);

        // Extract sellingUnit from the first batch
        const firstBatchUnit = transformedBatches?.[0]?.fullData?.sellingUnit ? transformedBatches?.[0]?.fullData?.sellingUnit : "N/A";
        setSellingUnit(firstBatchUnit);

        // Auto-select if only one batch is available
        if (transformedBatches?.length === 1) {
          const singleBatch = transformedBatches[0];
          const availableUnits = singleBatch?.fullData?.availableUnits || 0;

          // Check if available units is 0 and set error
          if (availableUnits === 0) {
            setBatchErrors((prev) => ({
              ...prev,
              [adjustments[0]?.id]: "This batch does not contain any units",
            }));
          }

          setAdjustments((prev) =>
            prev?.map((adj, index) =>
              index === 0
                ? {
                    ...adj,
                    batchId: singleBatch?.value || "",
                    availableUnits: availableUnits,
                    adjustmentQuantity: "",
                  }
                : adj
            )
          );
        }
      } else {
        setBatchOptions([]);
        setSellingUnit("");
      }
    } catch (error) {
      setBatchOptions([]);
      setSellingUnit("");
    } finally {
      setLoadingBatches(false);
    }
  };

  const getAvailableBatchOptions = (currentAdjustmentId) => {
    const selectedBatchIds = adjustments
      ?.filter((adj) => adj?.id !== currentAdjustmentId && adj?.batchId)
      ?.map((adj) => adj?.batchId);

    return batchOptions?.filter((option) => !selectedBatchIds?.includes(option?.value));
  };

  // Check if there are multiple batches
  const hasMultipleBatches = batchOptions?.length > 1;

  useEffect(() => {
    if (selectedProduct?.gtin) {
      fetchBatchData(selectedProduct?.gtin);
    } else {
      setBatchOptions([]);
      setSellingUnit("");
    }
  }, [selectedProduct?.gtin]);

  useEffect(() => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    const timer = setTimeout(() => {
      fetchProducts(productSearch);
    }, 500);

    setSearchTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [productSearch]);



  const handleAdjustmentChange = (id, field, value) => {
    setAdjustments((prev) =>
      prev?.map((adj) => (adj?.id === id ? { ...adj, [field]: value } : adj))
    );
  };

  const handleBatchSelect = (adjustmentId, selectedBatch) => {
    const availableUnits = selectedBatch?.fullData?.availableUnits || 0;

    // Check if available units is 0 and set error
    if (availableUnits === 0) {
      setBatchErrors((prev) => ({
        ...prev,
        [adjustmentId]: "This batch does not contain any units",
      }));
    } else {
      setBatchErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[adjustmentId];
        return newErrors;
      });
    }

    setAdjustments((prev) =>
      prev?.map((adj) =>
        adj?.id === adjustmentId
          ? {
              ...adj,
              batchId: selectedBatch?.value || "",
              availableUnits: availableUnits,
              adjustmentQuantity: "", // Reset adjustment quantity when batch changes
            }
          : adj
      )
    );

    // Clear quantity error when batch changes
    setQuantityErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[adjustmentId];
      return newErrors;
    });
  };

  // Add new adjustment row
  const handleAddMore = () => {
    setAdjustments((prev) => [
      ...prev,
      {
        id: Date.now(),
        batchId: "",
        availableQuantity: "",
        adjustmentType: "",
        adjustmentQuantity: "",
        reason: "",
        images: [],
      },
    ]);
  };

  const handleRemoveAdjustment = (id) => {
    if (adjustments?.length > 1) {
      setAdjustments((prev) => prev?.filter((adj) => adj?.id !== id));
      // Remove errors for this adjustment
      setBatchErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
      setQuantityErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  // Remove base64 prefix
  const removeDataURLSuffix = (base64String) => {
    return base64String.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
  };

  const handleImageUpload = (adjustmentId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage((prev) => ({ ...prev, [adjustmentId]: true }));

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result;

      const imagePayload = {
        uploadType: "STOCK_ADJUSTMENT",
        images: {
          image1: removeDataURLSuffix(base64String),
        },
      };

      uploadImageBase64(imagePayload)
        .then((res) => {
          const data = res?.data?.data?.data;
          if (data && data?.image1) {
            setAdjustments((prev) =>
              prev.map((adj) =>
                adj?.id === adjustmentId
                  ? {
                      ...adj,
                      images: [
                        {
                          url: data?.image1,
                          name: file?.name,
                        },
                      ],
                    }
                  : adj
              )
            );
          } else {
            useAlert("Failed to upload image. Please try again.");
          }
        })
        .catch((err) => {
          useAlert("Error uploading image. Please try again.");
        })
        .finally(() => {
          setUploadingImage((prev) => ({ ...prev, [adjustmentId]: false }));
        });
    };

    reader.onerror = (error) => {
      useAlert("Error reading file. Please try again.");
      setUploadingImage((prev) => ({ ...prev, [adjustmentId]: false }));
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (adjustmentId, imageIndex) => {
    setAdjustments((prev) =>
      prev.map((adj) =>
        adj?.id === adjustmentId
          ? { ...adj, images: adj?.images?.filter((_, i) => i !== imageIndex) }
          : adj
      )
    );
  };

  // Handler for adjustment quantity with validation
  const handleAdjustmentQuantityChange = (adjustmentId, value) => {
    // Allow empty string for clearing the field
    if (value === "") {
      handleAdjustmentChange(adjustmentId, "adjustmentQuantity", value);
      setQuantityErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[adjustmentId];
        return newErrors;
      });
      return;
    }

    // Convert to number and check if it's negative
    const numValue = parseFloat(value);

    // Find the current adjustment to get available units
    const currentAdjustment = adjustments.find((adj) => adj.id === adjustmentId);
    const availableUnits = currentAdjustment?.availableUnits || 0;

    // Only update if the value is not negative
    if (!isNaN(numValue) && numValue >= 0) {
      handleAdjustmentChange(adjustmentId, "adjustmentQuantity", value);

      // Check if quantity exceeds available units
      if (numValue > availableUnits) {
        setQuantityErrors((prev) => ({
          ...prev,
          [adjustmentId]: `Quantity cannot exceed available units`,
        }));
      } else {
        setQuantityErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[adjustmentId];
          return newErrors;
        });
      }
    }
  };

  const handleSave = async () => {
    if (!selectedProduct) {
      useAlert("Please select a product");
      return;
    }

    const isValid = adjustments?.every(
      (adj) =>
        adj.batchId &&
        adj.availableUnits !== "" &&
        adj.adjustmentType &&
        adj.adjustmentQuantity !== "" &&
        adj.reason
    );

    if (!isValid) {
      useAlert("Please fill in all required fields for each adjustment");
      return;
    }

    // Validation to ensure only positive numbers
    const hasValidQuantities = adjustments?.every((adj) => {
      const quantity = parseFloat(adj?.adjustmentQuantity);
      return adj?.adjustmentQuantity !== "" && !isNaN(quantity) && quantity >= 0;
    });

    if (!hasValidQuantities) {
      useAlert("Please enter valid positive adjustment quantities");
      return;
    }

    // Validation to ensure quantity doesn't exceed available units
    const hasQuantityWithinLimit = adjustments?.every((adj) => {
      const quantity = parseFloat(adj?.adjustmentQuantity);
      const available = parseFloat(adj?.availableUnits);
      return quantity <= available;
    });

    if (!hasQuantityWithinLimit) {
      useAlert("Adjustment quantity cannot exceed available quantity");
      return;
    }

    const userDetails = retailUserLocalStorageData;
    const userId = userDetails?.uidx || userDetails?.id || "UNKNOWN_USER";
    const userName = userDetails?.firstName || userDetails?.name || "Unknown User";

    const payload = {
      locationId: locId,
      orgId: orgId,
      variantId: selectedProduct?.fullData?.variantId || "",
      gtin: selectedProduct?.gtin,
      batchAdjustments: adjustments?.map((adj) => ({
        batchNo: adj?.batchId,
        reason: adj?.reason,
        adjustmentType: adj?.adjustmentType || "SUBTRACT",
        adjustedQuantity: parseFloat(adj?.adjustmentQuantity),
        imageUrl: adj?.images && adj?.images?.length > 0 ? adj?.images?.[0]?.url : "",
      })),
      source: "POS ADJUSTMENT PAGE",
      sourceId: sessionId || "UNKNOWN_SESSION",
      adjustedBy: userId,
      adjustedByName: userName,
    };

    // Validate mandatory fields
    if (!payload.locationId || !payload.orgId || !payload.gtin) {
      useAlert("Missing required fields: locationId, orgId, or gtin");
      return;
    }

    if (!payload.source || !payload.sourceId || !payload.adjustedBy || !payload.adjustedByName) {
      useAlert("Missing required user information");
      return;
    }

    try {
      const response = await createAdjustmentLogs(payload);

      if (response?.data?.data?.statusCode === 200 || response?.data?.data?.es === 0) {
        useAlert("Stock adjustment created successfully!");
        setReset(!reset);
        handleCloseAndReset();
      } else {
        useAlert("Failed to create stock adjustment. Please try again.");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.data?.message || "Error creating stock adjustment";
      useAlert(errorMessage);
    }
  };

  const handleCloseAndReset = () => {
    setProductSearch("");
    setSelectedProduct(null);
    setProductOptions([]);
    setBatchOptions([]);
    setSellingUnit("");
    setBatchErrors({});
    setQuantityErrors({});
    setAdjustments([
      {
        id: Date.now(),
        batchId: "",
        availableQuantity: "",
        adjustmentType: "",
        adjustmentQuantity: "",
        reason: "",
        images: [],
      },
    ]);
    handleClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleCloseAndReset}>
      <div className="adj-modal-container">
        {/* Header */}
        <div className="adj-modal-header">
          <h2 className="adj-modal-title" id="add-stock-adjust-modal">
            Add Stock Adjustment
          </h2>
          <button
            type="button"
            className="adj-modal-close-btn"
            onClick={handleCloseAndReset}
            style={{ position: "absolute", top: "1rem", right: "1rem" }}
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
            <span className="sr-only">Close</span>
          </button>
        </div>

        <div className="adj-modal-content">
        {!hasMultipleBatches ? (
          /* Single Batch Layout - Matches Replit design exactly */
          <div className="grid gap-6 py-4" style={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: "1rem", marginBottom: "1rem" }}>
            <div className="adj-form-grid">
              {/* Item Name / SKU */}
              <div className="adj-field-group">
                <label className="adj-field-label">Item Name / SKU</label>
                <Autocomplete
                  fullWidth
                  aria-required
                  filterOptions={(options) => options}
                  options={productOptions}
                  loading={loadingProducts}
                  value={selectedProduct}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedProduct({
                        id: newValue?.id,
                        name: newValue?.name,
                        gtin: newValue?.gtin,
                        fullData: newValue?.fullData,
                      });

                      // Reset adjustments when product changes
                      setBatchErrors({});
                      setQuantityErrors({});
                      setAdjustments([
                        {
                          id: Date.now(),
                          batchId: "",
                          availableQuantity: "",
                          adjustmentType: "",
                          adjustmentQuantity: "",
                          reason: "",
                          images: [],
                        },
                      ]);
                    } else {
                      setSelectedProduct(null);
                      setProductOptions([]);
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input" && !selectedProduct) {
                      setProductSearch(newInputValue);
                    } else if (reason === "clear") {
                      setProductSearch("");
                      setSelectedProduct(null);
                      setProductOptions([]);
                      setBatchOptions([]);
                      setBatchErrors({});
                      setQuantityErrors({});
                    }
                  }}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    if (typeof option === "string") return option;
                    const name = option?.name || option?.gtin || "Unknown Product";
                    const gtin = option?.gtin || "";
                    return gtin ? `${name} (${gtin})` : name;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.gtin?.toString() === value?.gtin?.toString() ||
                    option?.id?.toString() === value?.id?.toString()
                  }
                  noOptionsText={
                    loadingProducts
                      ? "Searching..."
                      : productSearch?.length < 2
                      ? "Type at least 2 characters to search"
                      : "No products found"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search item..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0.375rem",
                          height: "36px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{option?.name}</div>
                      </div>
                    </li>
                  )}
                />
              </div>

              {/* Type */}
              <div className="adj-field-group">
                <label className="adj-field-label">Type</label>
                <Select
                  options={adjustmentTypeOptions}
                  value={
                    adjustmentTypeOptions?.find(
                      (opt) => opt?.value === adjustments[0]?.adjustmentType
                    ) || null
                  }
                  onChange={(selectedOption) =>
                    handleAdjustmentChange(
                      adjustments[0]?.id,
                      "adjustmentType",
                      selectedOption?.value || ""
                    )
                  }
                  placeholder="Select type"
                  styles={customSelectStyles}
                  isClearable
                />
              </div>
            </div>

            {/* Batch error message if any */}
            {batchErrors[adjustments[0]?.id] && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  marginTop: "-0.5rem"
                }}
              >
                {batchErrors[adjustments[0]?.id]}
              </div>
            )}

            <div className="adj-form-grid">
              {/* Quantity */}
              <div className="adj-field-group">
                <label className="adj-field-label">Quantity</label>
                <input
                  className="adj-field-input"
                  type="number"
                  placeholder="0.00"
                  value={adjustments[0]?.adjustmentQuantity}
                  onChange={(e) => handleAdjustmentQuantityChange(adjustments[0]?.id, e.target.value)}
                  min="0"
                  step="any"
                />
                {quantityErrors[adjustments[0]?.id] && (
                  <div
                    style={{
                      marginTop: "0.25rem",
                      fontSize: "0.75rem",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    {quantityErrors[adjustments[0]?.id]}
                  </div>
                )}
              </div>

              {/* Unit */}
              <div className="adj-field-group">
                <label className="adj-field-label">Unit</label>
                <input
                  className="adj-field-input"
                  value={sellingUnit}
                  readOnly
                  style={{ backgroundColor: "#f8fafc" }}
                />
              </div>
            </div>

            {/* Reason / Notes */}
            <div className="adj-field-group">
              <label className="adj-field-label">Reason / Notes</label>
              <Select
                options={reasonOptions}
                value={reasonOptions?.find((opt) => opt?.value === adjustments[0]?.reason) || null}
                onChange={(selectedOption) =>
                  handleAdjustmentChange(adjustments[0]?.id, "reason", selectedOption?.value || "")
                }
                placeholder="Describe why this adjustment is being made..."
                styles={customSelectStyles}
                isClearable
              />
            </div>

            {/* Image */}
            <div className="adj-field-group">
              <label className="adj-field-label">Image</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                {adjustments[0]?.images?.map((image, imageIndex) => (
                  <div
                    key={imageIndex}
                    style={{
                      position: "relative",
                      width: "38px",
                      height: "38px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "0.375rem",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={image?.url}
                      alt={image?.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(adjustments[0]?.id, imageIndex)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        color: "white",
                        padding: "2px",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.7)",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}

                {uploadingImage[adjustments[0]?.id] && (
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "0.375rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <CircularProgress size={24} />
                  </div>
                )}

                {adjustments[0]?.images?.length === 0 && !uploadingImage[adjustments[0]?.id] && (
                  <label
                    htmlFor={`image-upload-${adjustments[0]?.id}`}
                    style={{
                      width: "38px",
                      height: "38px",
                      border: "2px dashed #cbd5e1",
                      borderRadius: "0.375rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <AddIcon sx={{ color: "#94a3b8" }} />
                    <input
                      id={`image-upload-${adjustments[0]?.id}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(adjustments[0]?.id, e)}
                      style={{ display: "none" }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Multi-Batch Layout - For products with multiple batch IDs */
          <>
            {/* Product Search */}
            <div className="adj-field-group" style={{ marginBottom: "1.5rem" }}>
              <label className="adj-field-label">Item Name / SKU</label>
              <Autocomplete
                fullWidth
                aria-required
                filterOptions={(options) => options}
                options={productOptions}
                loading={loadingProducts}
                value={selectedProduct}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedProduct({
                      id: newValue?.id,
                      name: newValue?.name,
                      gtin: newValue?.gtin,
                      fullData: newValue?.fullData,
                    });

                    // Reset adjustments when product changes
                    setBatchErrors({});
                    setQuantityErrors({});
                    setAdjustments([
                      {
                        id: Date.now(),
                        batchId: "",
                        availableQuantity: "",
                        adjustmentType: "",
                        adjustmentQuantity: "",
                        reason: "",
                        images: [],
                      },
                    ]);
                  } else {
                    setSelectedProduct(null);
                    setProductOptions([]);
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input" && !selectedProduct) {
                    setProductSearch(newInputValue);
                  } else if (reason === "clear") {
                    setProductSearch("");
                    setSelectedProduct(null);
                    setProductOptions([]);
                    setBatchOptions([]);
                    setBatchErrors({});
                    setQuantityErrors({});
                  }
                }}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  if (typeof option === "string") return option;
                  const name = option?.name || option?.gtin || "Unknown Product";
                  const gtin = option?.gtin || "";
                  return gtin ? `${name} (${gtin})` : name;
                }}
                isOptionEqualToValue={(option, value) =>
                  option?.gtin?.toString() === value?.gtin?.toString() ||
                  option?.id?.toString() === value?.id?.toString()
                }
                noOptionsText={
                  loadingProducts
                    ? "Searching..."
                    : productSearch?.length < 2
                    ? "Type at least 2 characters to search"
                    : "No products found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search item..."
                    size="small"
                    InputProps={{
                      ...params?.InputProps,
                      endAdornment: (
                        <>
                          {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params?.InputProps?.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0.375rem",
                        height: "36px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{option?.name}</div>
                    </div>
                  </li>
                )}
              />
            </div>

            {/* Show loading state or message when product is selected */}
            {selectedProduct && loadingBatches && (
              <div style={{ marginBottom: "1rem", textAlign: "center" }}>
                <CircularProgress size={20} />
                <span style={{ marginLeft: "0.5rem", fontSize: "0.875rem", color: "#666" }}>
                  Loading batch information...
                </span>
              </div>
            )}

            {/* Show message if no batches available */}
            {selectedProduct && !loadingBatches && batchOptions?.length === 0 && (
              <div
                style={{
                  marginBottom: "1rem",
                  padding: "0.75rem",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                }}
              >
                No batches available for this product
              </div>
            )}

            {/* Batch Ids & Adjustments mapping container */}
            <div
              style={{
                marginBottom: "1.5rem",
                padding: "1rem",
                borderRadius: "0.375rem",
              }}
            >
              <h4
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                }}
              >
                Batch Ids & Adjustments
              </h4>

              {adjustments?.map((adjustment, index) => (
                <div
                  key={adjustment?.id}
                  className="adj-adjustment-item-card"
                  style={{
                    marginBottom: index === adjustments.length - 1 ? "0" : "1.5rem",
                    padding: "1rem",
                    // border: "1px solid #cbd5e1",
                    // borderRadius: "0.375rem",
                    position: "relative",
                    // 
                    display: "grid",
                    gridTemplateColumns:"2fr 2fr",
                    gap:"10px",
                    borderBottom: index === adjustments.length - 1 ? "none" : "#8e9195 1px dashed",
                  }}
                >
                  {/* Remove button for additional rows */}
                  {adjustments?.length > 1 && (
                    <div className="adj-adjustment-item-header">
                      <span>Adjustment #{index + 1}</span>
                      <button
                        type="button"
                        className="adj-btn-remove"
                        onClick={() => handleRemoveAdjustment(adjustment?.id)}
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  {/* Batch ID and Type row */}
                  <div className="adj-form-grid">
                    <div className="adj-field-group">
                      <label className="adj-field-label">Batch ID</label>
                      <Select
                        options={getAvailableBatchOptions(adjustment?.id)}
                        value={
                          getAvailableBatchOptions(adjustment?.id)?.find(
                            (opt) => opt?.value === adjustment?.batchId
                          ) || null
                        }
                        onChange={(selectedOption) => handleBatchSelect(adjustment?.id, selectedOption)}
                        placeholder="Select batch ID"
                        styles={customSelectStyles}
                        isClearable
                      />
                      {batchErrors[adjustment?.id] && (
                        <div
                          style={{
                            marginTop: "0.25rem",
                            fontSize: "0.75rem",
                            color: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                          }}
                        >
                         {batchErrors[adjustment?.id]}
                        </div>
                      )}
                    </div>

                    <div className="adj-field-group">
                      <label className="adj-field-label">Type</label>
                      <Select
                        options={adjustmentTypeOptions}
                        value={
                          adjustmentTypeOptions?.find(
                            (opt) => opt?.value === adjustment?.adjustmentType
                          ) || null
                        }
                        onChange={(selectedOption) =>
                          handleAdjustmentChange(
                            adjustment?.id,
                            "adjustmentType",
                            selectedOption?.value || ""
                          )
                        }
                        placeholder="Select type"
                        styles={customSelectStyles}
                        isClearable
                      />
                    </div>
                  </div>

                  {/* Quantity and Unit row */}
                  <div className="adj-form-grid">
                    <div className="adj-field-group">
                      <label className="adj-field-label">Quantity</label>
                      <input
                        className="adj-field-input"
                        type="number"
                        placeholder="0.00"
                        value={adjustment?.adjustmentQuantity}
                        onChange={(e) => handleAdjustmentQuantityChange(adjustment?.id, e.target.value)}
                        min="0"
                        step="any"
                      />
                      {quantityErrors[adjustment?.id] && (
                        <div
                          style={{
                            marginTop: "0.25rem",
                            fontSize: "0.75rem",
                            color: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                          }}
                        >
                          {quantityErrors[adjustment?.id]}
                        </div>
                      )}
                    </div>

                    <div className="adj-field-group">
                      <label className="adj-field-label">Unit</label>
                      <input
                        className="adj-field-input"
                        readOnly
                        value={sellingUnit}
                        style={{ backgroundColor: "#f8fafc" }}
                      />
                    </div>
                  </div>

                  {/* Reason Select dropdown */}
                  <div className="adj-field-group" style={{ marginBottom: "1rem" }}>
                    <label className="adj-field-label">Reason / Notes</label>
                    <Select
                      options={reasonOptions}
                      value={reasonOptions?.find((opt) => opt?.value === adjustment?.reason) || null}
                      onChange={(selectedOption) =>
                        handleAdjustmentChange(adjustment?.id, "reason", selectedOption?.value || "")
                      }
                      placeholder="Describe why this adjustment is being made..."
                      styles={customSelectStyles}
                      isClearable
                    />
                  </div>

                  {/* Image Upload Section */}
                  <div className="adj-field-group">
                    <label className="adj-field-label">Image</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                      {adjustment?.images?.map((image, imageIndex) => (
                        <div
                          key={imageIndex}
                          style={{
                            position: "relative",
                            width: "38px",
                            height: "38px",
                            border: "1px solid #cbd5e1",
                            borderRadius: "0.375rem",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={image?.url}
                            alt={image?.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveImage(adjustment?.id, imageIndex)}
                            sx={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              backgroundColor: "rgba(0,0,0,0.5)",
                              color: "white",
                              padding: "2px",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.7)",
                              },
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))}

                      {uploadingImage[adjustment?.id] && (
                        <div
                          style={{
                            width: "38px",
                            height: "38px",
                            border: "1px solid #cbd5e1",
                            borderRadius: "0.375rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <CircularProgress size={24} />
                        </div>
                      )}

                      {adjustment?.images?.length === 0 && !uploadingImage[adjustment?.id] && (
                        <label
                          htmlFor={`image-upload-${adjustment?.id}`}
                          style={{
                            width: "38px",
                            height: "38px",
                            border: "2px dashed #cbd5e1",
                            borderRadius: "0.375rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <AddIcon sx={{ color: "#94a3b8" }} />
                          <input
                            id={`image-upload-${adjustment?.id}`}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(adjustment?.id, e)}
                            style={{ display: "none" }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Add More Button */}
              {hasMultipleBatches && (
                <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", marginTop: "0.5rem" }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddMore}
                    sx={{
                      textTransform: "none",
                      borderRadius: "0.375rem",
                      borderColor: "#2563eb",
                      color: "#2563eb",
                      "&:hover": {
                        borderColor: "#1d4ed8",
                        backgroundColor: "#eff6ff",
                      },
                    }}
                  >
                    Add More
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        </div>

        {/* Action Buttons */}
        <div className="adj-modal-footer">
          <button
            type="button"
            className="adj-btn-cancel"
            onClick={handleCloseAndReset}
          >
            Cancel
          </button>
          <button
            type="button"
            className="adj-btn-save"
            onClick={handleSave}
            disabled={!selectedProduct || batchOptions?.length === 0}
          >
            Save Adjustment
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default AdjustStockCreationModal;
